import { __decorate } from "tslib";
import { html } from 'lit';
import { CipublicElement } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/mixins/base.js';
import { property, queryAll, state } from 'lit/decorators.js';
import { gridBreakpoints } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/breakpoints.js';
import { addStyleSheetToElements } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/cssstylesheet-interface.js';
import { InternalI18n } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/i18n/internal-i18n-controller.js';
import { emit } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/event.js';
import '@cigna/cigna_csg_gsg_cipublic-button/cipublic-button.js';
import '@cigna/cigna_csg_gsg_cipublic-icon/cipublic-icon.js';
import { locales } from './locales.js';
import styles from './pagination.scss.js';
import buttons from './buttons.scss.js';
/**
 * @element cipublic-pagination
 */
export class Pagination extends CipublicElement {
    constructor() {
        super(...arguments);
        this.i18n = new InternalI18n(this, locales);
        this._currentPage = 1;
        /**
         * how many elements will display per page, indicated by business to typically be 20
         */
        this.pageSize = 20;
        /**
         * how many buttons are displayed on the page at once
         */
        this.visiblePages = 10;
    }
    get currentPage() {
        return this._currentPage;
    }
    set currentPage(newPage) {
        const oldPage = this._currentPage;
        this._currentPage = newPage;
        this.requestUpdate('currentPage', oldPage);
    }
    get realVisiblePages() {
        const visiblePages = {
            xs: 3,
            sm: 5,
            md: 7,
        };
        // lg and up
        if (window.matchMedia(`(min-width: ${gridBreakpoints.lg}px)`).matches) {
            return this.visiblePages;
        }
        if (window.matchMedia(`(min-width: ${gridBreakpoints.md}px)`).matches) {
            return Math.min(visiblePages.md, this.visiblePages);
        }
        if (window.matchMedia(`(min-width: ${gridBreakpoints.sm}px)`).matches) {
            return Math.min(visiblePages.sm, this.visiblePages);
        }
        if (window.matchMedia(`(min-width: ${gridBreakpoints.xs}px)`).matches) {
            return Math.min(visiblePages.xs, this.visiblePages);
        }
        return this.visiblePages;
    }
    get totalPages() {
        return Math.ceil(this.totalResults / this.pageSize);
    }
    get hasPreviousPage() {
        return this.currentPage <= 1;
    }
    get hasNextPage() {
        return this.currentPage >= this.totalPages;
    }
    // get range of pages to display [3, 4, 5], [2, 3, 4, 5]
    get pageRange() {
        const left = Math.floor(this.realVisiblePages / 2);
        let startPage = this.currentPage - left;
        startPage = Math.min(startPage, this.totalPages - this.realVisiblePages + 1);
        startPage = Math.max(startPage, 1);
        const endPage = Math.min(startPage + this.realVisiblePages - 1, this.totalPages);
        return [...Array(this.totalPages)].map((_, index) => index + 1).slice(startPage - 1, endPage);
    }
    gotoPage(page, event) {
        event.target.blur(); // On mouseup only
        this.currentPage = page;
        if (this.currentPage > this.totalPages) {
            this.currentPage = 1;
        }
        if (this.currentPage < 1) {
            this.currentPage = this.totalPages;
        }
        emit(event.target, 'pagination.click', {
            detail: { clickEvent: event, newPage: this.currentPage },
        });
        this.updatePageButtons();
    }
    async updatePageButtons() {
        await this.updateComplete;
        this._buttonList.forEach((_element) => {
            var _a;
            const elementBtn = (_a = _element.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('button');
            if (this.currentPage.toString() === _element.id) {
                elementBtn === null || elementBtn === void 0 ? void 0 : elementBtn.setAttribute('aria-current', 'true');
                // when using with apps, focus should not be sent back down to the new page button
                // elementBtn?.focus();
            }
            else {
                elementBtn === null || elementBtn === void 0 ? void 0 : elementBtn.removeAttribute('aria-current');
            }
        });
    }
    handleResize() {
        addStyleSheetToElements(this._buttonList, buttons);
        this.requestUpdate();
    }
    static get styles() {
        return [styles];
    }
    disconnectedCallback() {
        window.removeEventListener('resize', this.handleResize.bind(this));
        super.disconnectedCallback();
    }
    async firstUpdated() {
        await this.updateComplete;
        this.windowWidth = window.innerWidth;
        addStyleSheetToElements(this._buttonList, buttons);
        this.updatePageButtons();
        window.addEventListener('resize', () => {
            if (this.windowWidth !== window.innerWidth) {
                this.windowWidth = window.innerWidth;
                this.handleResize.bind(this);
            }
        });
    }
    render() {
        if (this.totalPages > 0) {
            return html `<cipublic-button size="small" class="first" type="button" variant="secondary" ?disabled="${this.hasPreviousPage}" @click="${(event) => this.gotoPage(1, event)}">${this.i18n.t('first_page')}</cipublic-button><cipublic-button icononly="" size="default" class="previous" type="button" variant="secondary" ?disabled="${this.hasPreviousPage}" @click="${(event) => this.gotoPage(this.currentPage - 1, event)}"><cipublic-icon set="system" name="chevron-left" aria-label="${this.i18n.t('previous_page')}" role="img"></cipublic-icon></cipublic-button><ul>${this.pageRange.map((page) => html `<li><cipublic-button @click="${(event) => this.gotoPage(page, event)}" @mouseup="${(event) => {
                event.target.blur();
            }}" id="${page.toString()}" type="button" variant="tag-chicklet">${page}</cipublic-button></li>`)}</ul><cipublic-button icononly="" size="default" class="next" type="button" variant="secondary" ?disabled="${this.hasNextPage}" @click="${(event) => this.gotoPage(this.currentPage + 1, event)}"><cipublic-icon set="system" name="chevron-right" aria-label="${this.i18n.t('next_page')}" role="img"></cipublic-icon></cipublic-button><cipublic-button size="small" class="last" type="button" variant="secondary" ?disabled="${this.hasNextPage}" @click="${(event) => this.gotoPage(this.totalPages, event)}">${this.i18n.t('last_page')}</cipublic-button>`;
        }
        else {
            return html ``;
        }
    }
}
__decorate([
    property({ reflect: true, type: Number, attribute: 'current-page' })
], Pagination.prototype, "currentPage", null);
__decorate([
    property({ reflect: true, type: Number, attribute: 'total-results' })
], Pagination.prototype, "totalResults", void 0);
__decorate([
    property({ reflect: true, type: Number, attribute: 'page-size' })
], Pagination.prototype, "pageSize", void 0);
__decorate([
    property({ reflect: true, type: Number, attribute: 'visible-pages' })
], Pagination.prototype, "visiblePages", void 0);
__decorate([
    queryAll('ul li cipublic-button')
], Pagination.prototype, "_buttonList", void 0);
__decorate([
    state()
], Pagination.prototype, "windowWidth", void 0);
//# sourceMappingURL=pagination.js.map